/**
 * Secure Handshake Service
 * Implements a secure handshake protocol for postMessage communication
 * Similar to Postmate's approach: https://github.com/dollarshaveclub/postmate
 * 
 * Security Features:
 * 1. Token-based verification (prevents message injection)
 * 2. Origin verification (both sides verify each other)
 * 3. Connection state tracking (only accept messages after handshake)
 * 4. Timeout protection (handshake must complete within time limit)
 */

import {
  MessageType,
} from '@/types';
import type {
  HandshakeRequestMessage,
  HandshakeResponseMessage,
  HandshakeCompleteMessage,
} from '@/types';

export enum HandshakeState {
  /** Handshake not started */
  PENDING = 'pending',
  /** Handshake request sent */
  REQUEST_SENT = 'request_sent',
  /** Handshake response received */
  RESPONSE_RECEIVED = 'response_received',
  /** Handshake complete */
  COMPLETE = 'complete',
  /** Handshake failed */
  FAILED = 'failed',
}

export interface HandshakeConfig {
  /** Timeout in milliseconds (default: 5000) */
  timeout?: number;
  /** Enable debug logging */
  debug?: boolean;
}

export interface HandshakeCallbacks {
  /** Called when handshake completes successfully */
  onComplete?: (token: string) => void;
  /** Called when handshake fails */
  onError?: (error: Error) => void;
}

/**
 * Generate a secure random token
 */
export function generateHandshakeToken(): string {
  // Use crypto.randomUUID if available, otherwise fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: timestamp + random string
  return `handshake_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Handshake service for widget (iframe side)
 */
export class WidgetHandshakeService {
  private state: HandshakeState = HandshakeState.PENDING;
  private token: string | null = null;
  private parentOrigin: string | null = null;
  private timeoutId: NodeJS.Timeout | null = null;
  private debug: boolean;
  private callbacks: HandshakeCallbacks;

  constructor(config: HandshakeConfig = {}, callbacks: HandshakeCallbacks = {}) {
    this.debug = config.debug || false;
    this.callbacks = callbacks;
  }

  /**
   * Handle handshake request from parent
   */
  public handleHandshakeRequest(
    message: HandshakeRequestMessage,
    eventOrigin: string
  ): HandshakeResponseMessage | null {
    this.log('Handshake request received', { message, eventOrigin });

    if (this.state !== HandshakeState.PENDING) {
      this.log('Handshake already in progress, ignoring request', { currentState: this.state });
      return null;
    }

    // Validate token exists
    if (!message.data?.token) {
      this.fail(new Error('Handshake request missing token'));
      return null;
    }

    // Store token and parent origin
    this.token = message.data.token;
    this.parentOrigin = message.data.parentOrigin || eventOrigin;

    // Verify parent origin matches
    if (message.data.parentOrigin && message.data.parentOrigin !== eventOrigin) {
      this.fail(new Error(`Parent origin mismatch: ${message.data.parentOrigin} !== ${eventOrigin}`));
      return null;
    }

    // Update state
    this.state = HandshakeState.RESPONSE_RECEIVED;

    // Create response
    const response: HandshakeResponseMessage = {
      type: MessageType.HANDSHAKE_RESPONSE,
      timestamp: new Date().toISOString(),
      messageId: `handshake_resp_${Date.now()}`,
      origin: window.location.origin,
      data: {
        token: this.token,
        widgetOrigin: window.location.origin,
      },
    };

    this.log('Handshake response created', { response });
    return response;
  }

  /**
   * Handle handshake complete from parent
   */
  public handleHandshakeComplete(
    message: HandshakeCompleteMessage,
    eventOrigin: string
  ): boolean {
    this.log('Handshake complete received', { message, eventOrigin });

    if (this.state !== HandshakeState.RESPONSE_RECEIVED) {
      this.log('Handshake not in correct state for complete', { currentState: this.state });
      return false;
    }

    // Verify token matches
    if (!this.token || message.data?.token !== this.token) {
      this.fail(new Error('Handshake complete token mismatch'));
      return false;
    }

    // Verify origin matches
    if (this.parentOrigin && eventOrigin !== this.parentOrigin) {
      this.fail(new Error(`Handshake complete origin mismatch: ${eventOrigin} !== ${this.parentOrigin}`));
      return false;
    }

    // Clear timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Complete handshake
    this.state = HandshakeState.COMPLETE;
    this.log('Handshake completed successfully', { token: this.token });

    if (this.callbacks.onComplete) {
      this.callbacks.onComplete(this.token);
    }

    return true;
  }

  /**
   * Check if handshake is complete
   */
  public isComplete(): boolean {
    return this.state === HandshakeState.COMPLETE;
  }

  /**
   * Get current state
   */
  public getState(): HandshakeState {
    return this.state;
  }

  /**
   * Get handshake token
   */
  public getToken(): string | null {
    return this.token;
  }

  /**
   * Get parent origin
   */
  public getParentOrigin(): string | null {
    return this.parentOrigin;
  }

  /**
   * Reset handshake (for reconnection)
   */
  public reset(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.state = HandshakeState.PENDING;
    this.token = null;
    this.parentOrigin = null;
  }

  /**
   * Fail handshake
   */
  private fail(error: Error): void {
    this.state = HandshakeState.FAILED;
    this.log('Handshake failed', { error: error.message });

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.callbacks.onError) {
      this.callbacks.onError(error);
    }
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: unknown): void {
    if (this.debug) {
      console.log(`[WidgetHandshake] ${message}`, data || '');
    }
  }
}

/**
 * Handshake service for parent (loader side)
 */
export class ParentHandshakeService {
  private state: HandshakeState = HandshakeState.PENDING;
  private token: string | null = null;
  private widgetOrigin: string | null = null;
  private timeout: number;
  private timeoutId: NodeJS.Timeout | null = null;
  private debug: boolean;
  private callbacks: HandshakeCallbacks;

  constructor(
    _targetWindow: Window, // Reserved for future use
    widgetOrigin: string,
    config: HandshakeConfig = {},
    callbacks: HandshakeCallbacks = {}
  ) {
    this.widgetOrigin = widgetOrigin;
    this.timeout = config.timeout || 5000;
    this.debug = config.debug || false;
    this.callbacks = callbacks;
  }

  /**
   * Start handshake (send request to widget)
   */
  public startHandshake(): HandshakeRequestMessage {
    if (this.state !== HandshakeState.PENDING) {
      throw new Error('Handshake already started');
    }

    // Generate token
    this.token = generateHandshakeToken();
    this.state = HandshakeState.REQUEST_SENT;

    // Create request
    const request: HandshakeRequestMessage = {
      type: MessageType.HANDSHAKE_REQUEST,
      timestamp: new Date().toISOString(),
      messageId: `handshake_req_${Date.now()}`,
      origin: window.location.origin,
      data: {
        token: this.token,
        parentOrigin: window.location.origin,
      },
    };

    this.log('Handshake request created', { request });

    // Set timeout
    this.timeoutId = setTimeout(() => {
      this.fail(new Error('Handshake timeout'));
    }, this.timeout);

    return request;
  }

  /**
   * Handle handshake response from widget
   */
  public handleHandshakeResponse(
    message: HandshakeResponseMessage,
    eventOrigin: string
  ): HandshakeCompleteMessage | null {
    this.log('Handshake response received', { message, eventOrigin });

    if (this.state !== HandshakeState.REQUEST_SENT) {
      this.log('Handshake not in correct state for response', { currentState: this.state });
      return null;
    }

    // Verify token matches
    if (!this.token || message.data?.token !== this.token) {
      this.fail(new Error('Handshake response token mismatch'));
      return null;
    }

    // Verify widget origin matches
    if (this.widgetOrigin && eventOrigin !== this.widgetOrigin) {
      this.fail(new Error(`Widget origin mismatch: ${eventOrigin} !== ${this.widgetOrigin}`));
      return null;
    }

    // Verify widget origin in response
    if (message.data?.widgetOrigin && message.data.widgetOrigin !== eventOrigin) {
      this.fail(new Error(`Widget origin in response mismatch: ${message.data.widgetOrigin} !== ${eventOrigin}`));
      return null;
    }

    // Update state
    this.state = HandshakeState.RESPONSE_RECEIVED;

    // Create complete message
    const complete: HandshakeCompleteMessage = {
      type: MessageType.HANDSHAKE_COMPLETE,
      timestamp: new Date().toISOString(),
      messageId: `handshake_complete_${Date.now()}`,
      origin: window.location.origin,
      data: {
        token: this.token,
      },
    };

    this.log('Handshake complete created', { complete });

    // Clear timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Complete handshake
    this.state = HandshakeState.COMPLETE;
    this.log('Handshake completed successfully', { token: this.token });

    if (this.callbacks.onComplete) {
      this.callbacks.onComplete(this.token);
    }

    return complete;
  }

  /**
   * Check if handshake is complete
   */
  public isComplete(): boolean {
    return this.state === HandshakeState.COMPLETE;
  }

  /**
   * Get current state
   */
  public getState(): HandshakeState {
    return this.state;
  }

  /**
   * Get handshake token
   */
  public getToken(): string | null {
    return this.token;
  }

  /**
   * Reset handshake (for reconnection)
   */
  public reset(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.state = HandshakeState.PENDING;
    this.token = null;
  }

  /**
   * Fail handshake
   */
  private fail(error: Error): void {
    this.state = HandshakeState.FAILED;
    this.log('Handshake failed', { error: error.message });

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.callbacks.onError) {
      this.callbacks.onError(error);
    }
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: unknown): void {
    if (this.debug) {
      console.log(`[ParentHandshake] ${message}`, data || '');
    }
  }
}

